/* eslint-disable quotes */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Spin,
  DatePicker,
  Button,
  message,
  Card,
  Row,
  Col,
  Typography,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { dateFormat_dmy } from "../../../utils";
import Heading from "../../../components/Heading";
import TableContainer from "../../../components/TableContainer";
import { generateDynamicColumns } from "../../../utils/columnGenerator";
import useReportFilters from "../../../hooks/useReportFilters";
import { FilterOutlined, FileTextOutlined } from "@ant-design/icons";
import constants from "../../../constants/constants";
import "./style.scss";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};
const { HUB_OUTSCAN_CP_INSCAN_REPORT, HUB_OUTSCAN_CP_INSCAN_REPORT_DOWNLOAD } =
  urls;

const HubOutscanCPInscanReport = () => {
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handlePageChange,
    pageNum,
    pageSize,
    setPageNum,
  } = useReportFilters({
    from_hub_id: undefined,
    cp_id: undefined,
  }, 500);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState("-");
  const [returnedRecords, setReturnedRecords] = useState("-");
  const [dateRange, setDateRange] = useState({
    startDate: moment(),
    endDate: moment(),
  });
  const [hubs, setHubs] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [refreshSchedule, setRefreshSchedule] = useState(null);

  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const userRole = userinfo?.roles?.[0];
  const isCPUser = [
    constants.ROLES.CP_ADMIN,
    constants.ROLES.CP_OPERATOR,
    constants.ROLES.DP_OPERATOR,
  ].includes(userRole);
  const getPartnerIdInfo = () => {
    if (userRole === constants.ROLES.CP_ADMIN || userRole === constants.ROLES.CP_OPERATOR) {
      return { apiParam: "cpId", downloadParam: "cpId", value: userinfo?.cpId || null };
    } else if (userRole === constants.ROLES.DP_OPERATOR) {
      const deliveryPartners = JSON.parse(sessionStorage.getItem("deliveryPartner") || "{}")?.data || [];
      const dpId = deliveryPartners.length > 0 ? deliveryPartners[0]?.dpId : null;
      return { apiParam: "dpId", downloadParam: "dpId", value: dpId };
    }
    return { apiParam: "cpId", downloadParam: "cpId", value: null };
  };
  const partnerInfo = getPartnerIdInfo();

  const fetchHubs = async () => {
    try {
      const result = await axios.get(urls.GET_HUB_LIST_FOR_OUTSCAN);
      const response = result?.data;
      if (response) {
        const hubList = Array.isArray(response) ? response : (response.data || []);
        setHubs(hubList);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchChannelPartners = async () => {
    try {
      const result = await API.Api(urls.DRS_STATUS_CHANNEL_PARTNER_LIST).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        const cpList = Array.isArray(response) ? response : (response.data || []);
        setChannelPartners(cpList);
      }
    } catch (error) {
      console.error("Error fetching channel partners:", error);
    }
  };

  useEffect(() => {
    fetchHubs();
    fetchChannelPartners();
    fetchRefreshSchedule();
  }, []);

  const fetchRefreshSchedule = async () => {
    try {
      const res = await API.Api(urls.REFRESH_SCHEDULE_HUBOPS).get();
      if (res?.response?.data?.data?.[0]) {
        setRefreshSchedule(res.response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching refresh schedule:", error);
    }
  };

  const fetchReportData = async (page, size) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        fromDate: dateRange.startDate.format("YYYY-MM-DD"),
        toDate: dateRange.endDate.format("YYYY-MM-DD"),
        page: page - 1,
        size: size,
      });

      if (debouncedFilters.from_hub_id) {
        queryParams.append("fromHubId", debouncedFilters.from_hub_id);
      }
      if (isCPUser && partnerInfo.value) {
        queryParams.append(partnerInfo.apiParam, partnerInfo.value);
      } else if (debouncedFilters.cp_id) {
        queryParams.append("cpId", debouncedFilters.cp_id);
      }

      const result = await API.Api(
        `${HUB_OUTSCAN_CP_INSCAN_REPORT}?${queryParams.toString()}`,
      ).get();

      const response = result?.response?.data;

      if (result?.status && Array.isArray(response?.data)) {
        setTableData(response.data);
        setTotal(response.pagination?.totalRecords || 0);
        setReturnedRecords(response.data.length || 0);
      } else {
        setTableData([]);
        setTotal(0);
        message.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching Hub Outscan CP Inscan Report:", error);
      message.error("Error fetching report data");
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (tableData.length === 0) {
      message.error("No data available to download");
      return;
    }

    const queryParams = new URLSearchParams({
      fromDate: dateRange.startDate.format("YYYY-MM-DD"),
      toDate: dateRange.endDate.format("YYYY-MM-DD"),
    });

    if (debouncedFilters.from_hub_id) {
      queryParams.append("fromHubId", debouncedFilters.from_hub_id);
    }
    if (isCPUser && partnerInfo.value) {
      queryParams.append("cpId", partnerInfo.value);
    } else if (debouncedFilters.cp_id) {
      queryParams.append("cpId", debouncedFilters.cp_id);
    }

    const userData = JSON.parse(sessionStorage.getItem("userinfo") || localStorage.getItem("userinfo") || "{}");

    try {
      const response = await axios.get(
        `${HUB_OUTSCAN_CP_INSCAN_REPORT_DOWNLOAD}?${queryParams.toString()}`,
        {
          responseType: "blob",
          headers: { Authorization: "Bearer " + userData?.accessToken },
        },
      );

      if (response?.status === 200 && response?.data) {
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Hub_Outscan_CP_Inscan_Report_${dateRange.startDate.format("YYYY-MM-DD")}_to_${dateRange.endDate.format("YYYY-MM-DD")}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Download successful");
      }
    } catch (error) {
      message.error("Failed to download report");
    }
  };

  useEffect(() => {
    fetchReportData(pageNum, pageSize);
  }, [pageNum, pageSize, dateRange, debouncedFilters]);

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({
        startDate: dates[0],
        endDate: dates[1],
      });
      setPageNum(1);
    }
  };

  const staticColumns = [
    {
      title: "SNO",
      dataIndex: "_sno",
      key: "_sno",
      width: 60,
      render: (text) => text ?? "-",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "From Hub",
      dataIndex: "from_hub",
      key: "from_hub",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "CP Name",
      dataIndex: "cp_name",
      key: "cp_name",
      width: 180,
      render: (text) => text || "-",
    },
    {
      title: "Outscan by Hub to CP",
      dataIndex: "outscan_by_hub_to_cp",
      key: "outscan_by_hub_to_cp",
      width: 160,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "hoci-v2-outscan",
                date: record.date,
                fromHubId: record.from_hub_id,
                cpId: record.cp_id,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Total Outscan Weight",
      dataIndex: "total_outscan_weight",
      key: "total_outscan_weight",
      width: 160,
      render: (text) => (text != null ? text : "-"),
    },
    {
      title: "Inscanned by Same CP",
      dataIndex: "inscanned_by_same_cp",
      key: "inscanned_by_same_cp",
      width: 170,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                date: record.date,
                fromHubId: record.from_hub_id,
                cpId: record.cp_id,
              });
              window.open(
                `/drs-clickable-detail?type=hoci-v2-inscan-same&${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Inscanned by Same CP %",
      dataIndex: "inscanned_by_same_cp_percentage",
      key: "inscanned_by_same_cp_percentage",
      width: 170,
      render: (text) => (text != null ? `${text}%` : "-"),
    },
    {
      title: "Inscanned by Other CP",
      dataIndex: "inscanned_by_other_cp",
      key: "inscanned_by_other_cp",
      width: 170,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                date: record.date,
                fromHubId: record.from_hub_id,
                cpId: record.cp_id,
              });
              window.open(
                `/drs-clickable-detail?type=hoci-v2-inscan-other&${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Inscanned by Other CP %",
      dataIndex: "inscanned_by_other_cp_percentage",
      key: "inscanned_by_other_cp_percentage",
      width: 170,
      render: (text) => (text != null ? `${text}%` : "-"),
    },
    {
      title: "Not Inscanned by CP",
      dataIndex: "not_inscanned_by_cp",
      key: "not_inscanned_by_cp",
      width: 160,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                date: record.date,
                fromHubId: record.from_hub_id,
                cpId: record.cp_id,
              });
              window.open(
                `/drs-clickable-detail?type=hoci-v2-not-inscanned&${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Not Inscanned by CP %",
      dataIndex: "not_inscanned_by_cp_percentage",
      key: "not_inscanned_by_cp_percentage",
      width: 170,
      render: (text) => (text != null ? `${text}%` : "-"),
    },
  ];

  const columns = React.useMemo(
    () => generateDynamicColumns(tableData, staticColumns),
    [tableData],
  );

  return (
    <div className="hub-outscan-cp-inscan-report-container">
      {/* ── Page Top Bar ── */}
      <div className="page-topbar">
        <div className="topbar-left">
          <span className="title-main">Hub Outscan CP Inscan Report</span>
          {refreshSchedule && (
            <div className="sync-indicators">
              <div className="sync-tag last-refresh">
                <span className="sync-label">Last Refresh</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_last_completion_time_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
              <div className="sync-tag next-sync">
                <span className="sync-label">Next Sync</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_next_run_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          <div className="filter-item">
            <span className="filter-label">Outscan Date</span>
            <RangePicker
              value={[dateRange.startDate, dateRange.endDate]}
              format={dateFormat_dmy}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              allowClear={false}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            className="download-btn"
          >
            Download
          </Button>
        </div>
      </div>

      {/* ── Summary Hero Band ── */}
      <div className="summary-hero-band">
        <div className="hero-left">
          <div className="hero-icon">
            <FileTextOutlined />
          </div>
          <div className="hero-text">
            <div className="hero-label">Inscan & Outscan Tracking</div>
            <div className="hero-desc">
              Monitoring scan integrity between hub operations and channel
              partners
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof total === "number" ? total.toLocaleString() : total}
            </div>
            <div className="hero-stat-label">Total Records</div>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof returnedRecords === "number"
                ? returnedRecords.toLocaleString()
                : returnedRecords}
            </div>
            <div className="hero-stat-label">Showing</div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="filters-panel">
        <div className="filters-header">
          <FilterOutlined className="filter-icon" />
          <span className="filters-title">Data Filters</span>
        </div>
        <div className="filters-body">
          <Row gutter={[16, 14]}>
            <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
              <span className="filter-field-label">From Hub</span>
              <Select
                allowClear
                showSearch
                placeholder="Select From Hub"
                style={{ width: "100%" }}
                value={filters.from_hub_id}
                onChange={(value) => handleFilterChange("from_hub_id", value)}
                optionFilterProp="children"
              >
                {hubs.map((hub, idx) => {
                  const label = hub?.premiseName || hub?.hub_name || (typeof hub === "string" ? hub : "");
                  const value = hub?.premiseId || hub?.hub_id || (typeof hub === "string" ? hub : idx);
                  return (
                    <Option key={value || idx} value={value}>
                      {String(label || value || idx)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            {!isCPUser && (
              <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
                <span className="filter-field-label">Channel Partner</span>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select CP"
                  style={{ width: "100%" }}
                  value={filters.cp_id}
                  onChange={(value) => handleFilterChange("cp_id", value)}
                  optionFilterProp="children"
                >
                  {channelPartners.map((cp, idx) => {
                    const label =
                      cp?.channel_partner || (typeof cp === "string" ? cp : "");
                    const value =
                      cp?.cp_id || (typeof cp === "string" ? cp : idx);
                    return (
                      <Option key={value || idx} value={value}>
                        {String(label || value || idx)}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* ── Data Table ── */}
      <Spin spinning={loading}>
        <div className="table-panel">
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: pageNum,
              pageSize: pageSize,
              total: typeof total === "number" ? total : 0,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "150", "200"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination) => handlePageChange(pagination.current, pagination.pageSize)}
            scroll={{ x: "max-content" }}
            rowKey={(record) => record._sno || Math.random()}
          />
        </div>
      </Spin>
    </div>
  );
};

export default HubOutscanCPInscanReport;
